<template>
  <WidgetCadre
    titre="Bienvenue"
    variante="bandeau"
    :chargement="chargement"
    :panne="panne"
    :reglable="reglable"
    @retirer="$emit('retirer')"
    @recharger="$emit('recharger')"
  >
    <div
      class="bandeau"
      :class="`bandeau--${moment}`"
    >
      <!-- Une onde de signal en filigrane : l'accueil d'un opérateur. Décor
           pur, masqué aux lecteurs d'écran. -->
      <svg
        class="bandeau__onde"
        viewBox="0 0 600 160"
        preserveAspectRatio="none"
        aria-hidden="true"
      >
        <path d="M0 96 C 60 40, 120 40, 180 96 S 300 152, 360 96 S 480 40, 540 96 S 600 124, 600 124" />
        <path d="M0 112 C 75 70, 150 70, 225 112 S 375 154, 450 112 S 600 70, 600 70" />
        <path d="M0 80 C 50 60, 100 60, 150 80 S 250 100, 300 80 S 400 60, 450 80 S 550 100, 600 80" />
      </svg>

      <div class="bandeau__texte">
        <p class="bandeau__salut">
          {{ salutation }}{{ prenom ? ` ${prenom}` : '' }}
        </p>
        <p class="bandeau__date">
          {{ dateLongue }}
        </p>
      </div>

      <div
        v-if="compteurs.length"
        class="bandeau__pastilles"
      >
        <component
          :is="compteur.lien ? 'button' : 'span'"
          v-for="compteur in compteurs"
          :key="compteur.cle"
          class="pastille"
          :class="{ 'pastille--calme': !compteur.valeur, 'pastille--lien': compteur.lien }"
          :type="compteur.lien ? 'button' : undefined"
          @click="compteur.lien && router.push(compteur.lien)"
        >
          <span class="pastille__valeur">{{ compteur.valeur }}</span>
          <span class="pastille__libelle">{{ compteur.libelle }}</span>
        </component>
      </div>
    </div>
  </WidgetCadre>
</template>

<script setup>
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import { useStore } from 'vuex'
import WidgetCadre from './WidgetCadre.vue'

/**
 * Le bandeau d'accueil : qui on est, quel jour, et ce qui attend.
 *
 * Les compteurs viennent du serveur, qui ne pose que ceux auxquels cette
 * personne a droit — un compteur absent n'est pas un zéro. Leurs libellés
 * aussi : le front n'a pas à savoir quels écrans existent derrière.
 *
 * La couleur des halos suit l'heure. C'est le seul endroit de l'accueil qui
 * change sans qu'on y touche, et il le fait trois fois par jour, pas en boucle.
 */

const props = defineProps({
    donnees: { type: Object, default: null },
    chargement: { type: Boolean, default: false },
    panne: { type: Boolean, default: false },
    reglable: { type: Boolean, default: false },
})

defineEmits(['retirer', 'recharger'])

const router = useRouter()
const store = useStore()

const maintenant = new Date()
const heure = maintenant.getHours()

const moment = heure < 12 ? 'matin' : heure < 18 ? 'journee' : 'soir'
const salutation = heure < 18 ? 'Bonjour' : 'Bonsoir'

const dateLongue = maintenant.toLocaleDateString('fr-FR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
})

/** Le prénom du serveur fait foi ; celui du magasin évite un bandeau anonyme le temps du chargement. */
const prenom = computed(() => props.donnees?.prenom || store.state.user?.prenom || '')

const compteurs = computed(() => props.donnees?.compteurs ?? [])
</script>

<style lang="scss" scoped>
.bandeau {
  --halo-a: #3b82f6;
  --halo-b: #7c3aed;

  position: relative;
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 18px 28px;
  flex-wrap: wrap;
  min-height: 100%;
  padding: 22px 26px 20px;
  background:
    radial-gradient(60% 140% at 100% 0%, color-mix(in srgb, var(--halo-a) 55%, transparent), transparent 70%),
    radial-gradient(45% 120% at 70% 120%, color-mix(in srgb, var(--halo-b) 50%, transparent), transparent 70%),
    #02264a;
  box-sizing: border-box;

  &--matin {
    --halo-a: #38bdf8;
    --halo-b: #f59e0b;
  }

  &--soir {
    --halo-a: #8b5cf6;
    --halo-b: #db2777;
  }

  &__onde {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;

    path {
      fill: none;
      stroke: #fff;
      stroke-width: 1.2;
      opacity: 0.12;
      vector-effect: non-scaling-stroke;
    }

    path:nth-child(2) { opacity: 0.08; }
    path:nth-child(3) { opacity: 0.05; }
  }

  &__texte {
    position: relative;
    min-width: 0;
  }

  &__salut {
    margin: 0;
    font-size: clamp(24px, 3vw, 34px);
    font-weight: 700;
    line-height: 1.1;
    letter-spacing: -0.02em;
    color: #fff;
  }

  &__date {
    margin: 6px 0 0;
    font-size: 14px;
    color: rgba(255, 255, 255, 0.78);

    &::first-letter { text-transform: uppercase; }
  }

  &__pastilles {
    position: relative;
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }
}

.pastille {
  display: inline-flex;
  align-items: baseline;
  gap: 7px;
  padding: 8px 14px;
  border: 1px solid rgba(255, 255, 255, 0.22);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.12);
  color: #fff;
  font: inherit;
  text-align: left;

  &--lien {
    cursor: pointer;
    transition: background-color 0.15s ease;

    &:hover { background: rgba(255, 255, 255, 0.22); }
  }

  &:focus-visible {
    outline: 2px solid #fff;
    outline-offset: 2px;
  }

  &--calme { opacity: 0.7; }

  &__valeur {
    font-size: 18px;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
  }

  &__libelle {
    font-size: 12.5px;
    color: rgba(255, 255, 255, 0.85);
  }
}

@media (prefers-reduced-motion: reduce) {
  .pastille--lien { transition: none; }
}
</style>
